import React, { useState } from 'react';

const CrystalStructureViewer = () => {
  const [icsdId, setIcsdId] = useState('');
  const [loading, setLoading] = useState(false);
  const [materialData, setMaterialData] = useState(null);
  
  // Mock database of crystal structures (in a real app, this would be fetched from a database)
  const crystalDatabase = {
    '12345': {
      formula: 'MnO2',
      spaceGroup: 'Pmmm',
      structure: 'Halite, Rock Salt-like',
      bandGap: '1.31 eV',
      formationEnergy: '-2.033 eV/atom',
      magneticOrdering: 'Antiferromagnetic',
      totalMagnetization: '0.00 μB/f.u.',
      experimentallyObserved: 'No',
      description: 'MnO2 is Halite, Rock Salt-like structured and crystallizes in the orthorhombic Pmmm space group. Mn4+ is bonded to six equivalent O2- atoms to form a mixture of corner and edge-sharing MnO6 octahedra. The corner-sharing octahedral tilt angles range from 1-6°. There are a spread of Mn-O bond lengths ranging from 1.84-2.17 Å.'
    },
    '67890': {
      formula: 'Fe2O3',
      spaceGroup: 'R-3c',
      structure: 'Corundum',
      bandGap: '2.2 eV',
      formationEnergy: '-3.154 eV/atom',
      magneticOrdering: 'Ferromagnetic',
      totalMagnetization: '4.5 μB/f.u.',
      experimentallyObserved: 'Yes',
      description: 'Fe2O3 (hematite) adopts the corundum structure with a hexagonal close-packed array of oxide ions with Fe3+ ions occupying two-thirds of the octahedral sites. Each Fe3+ is bonded to six O2- atoms, forming distorted octahedra.'
    },
    '24680': {
      formula: 'TiO2',
      spaceGroup: 'P42/mnm',
      structure: 'Rutile',
      bandGap: '3.0 eV',
      formationEnergy: '-4.812 eV/atom',
      magneticOrdering: 'Diamagnetic',
      totalMagnetization: '0.00 μB/f.u.',
      experimentallyObserved: 'Yes',
      description: 'TiO2 in the rutile phase has a tetragonal structure where each Ti atom is coordinated to six O atoms, and each O atom is coordinated to three Ti atoms. The TiO6 octahedra share edges along the c-axis to form chains.'
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      const data = crystalDatabase[icsdId];
      setMaterialData(data || null);
      setLoading(false);
    }, 500);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-red-800 text-white p-4">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold">Experimental-Catalysis-Hub.org</h1>
          <nav className="flex space-x-4">
            <a href="#" className="hover:underline">Publications</a>
            <a href="#" className="hover:underline">Catalyst Materials</a>
            <a href="#" className="hover:underline">About</a>
            <a href="#" className="hover:underline">Documentation</a>
          </nav>
        </div>
      </header>
      
      {/* Main content */}
      <main className="container mx-auto flex-grow p-4">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4">Crystal Structure Viewer</h2>
          
          <form onSubmit={handleSubmit} className="mb-6">
            <div className="flex items-end gap-4">
              <div className="flex-grow">
                <label htmlFor="icsd-id" className="block text-sm font-medium text-gray-700 mb-1">
                  Enter ICSD ID
                </label>
                <input
                  type="text"
                  id="icsd-id"
                  className="w-full px-4 py-2 border rounded-md"
                  value={icsdId}
                  onChange={(e) => setIcsdId(e.target.value)}
                  placeholder="e.g., 12345"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Try sample IDs: 12345, 67890, or 24680
                </p>
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Loading...' : 'View Structure'}
              </button>
            </div>
          </form>
          
          {materialData ? (
            <div className="grid md:grid-cols-2 gap-6">
              {/* 3D visualization placeholder */}
              <div className="border rounded-md p-4 flex items-center justify-center bg-gray-50 aspect-square relative">
                {/* This would be a 3D visualization in a real app */}
                <div className="text-center">
                  <div className="w-48 h-48 mx-auto">
                    {materialData.formula === 'MnO2' && (
                      <svg viewBox="0 0 200 200" className="w-full h-full">
                        <circle cx="100" cy="100" r="30" fill="#8C2D8F" />
                        <circle cx="130" cy="70" r="15" fill="#E83A3A" />
                        <circle cx="70" cy="70" r="15" fill="#E83A3A" />
                        <circle cx="130" cy="130" r="15" fill="#E83A3A" />
                        <circle cx="70" cy="130" r="15" fill="#E83A3A" />
                        <circle cx="100" cy="60" r="15" fill="#E83A3A" />
                        <circle cx="100" cy="140" r="15" fill="#E83A3A" />
                        <line x1="100" y1="100" x2="130" y2="70" stroke="#000" strokeWidth="2" />
                        <line x1="100" y1="100" x2="70" y2="70" stroke="#000" strokeWidth="2" />
                        <line x1="100" y1="100" x2="130" y2="130" stroke="#000" strokeWidth="2" />
                        <line x1="100" y1="100" x2="70" y2="130" stroke="#000" strokeWidth="2" />
                        <line x1="100" y1="100" x2="100" y2="60" stroke="#000" strokeWidth="2" />
                        <line x1="100" y1="100" x2="100" y2="140" stroke="#000" strokeWidth="2" />
                      </svg>
                    )}
                    {materialData.formula === 'Fe2O3' && (
                      <svg viewBox="0 0 200 200" className="w-full h-full">
                        <circle cx="100" cy="100" r="25" fill="#B25D00" />
                        <circle cx="140" cy="100" r="25" fill="#B25D00" />
                        <circle cx="120" cy="70" r="15" fill="#E83A3A" />
                        <circle cx="120" cy="130" r="15" fill="#E83A3A" />
                        <circle cx="80" cy="70" r="15" fill="#E83A3A" />
                        <line x1="100" y1="100" x2="140" y2="100" stroke="#000" strokeWidth="2" />
                        <line x1="100" y1="100" x2="120" y2="70" stroke="#000" strokeWidth="2" />
                        <line x1="140" y1="100" x2="120" y2="70" stroke="#000" strokeWidth="2" />
                        <line x1="100" y1="100" x2="120" y2="130" stroke="#000" strokeWidth="2" />
                        <line x1="140" y1="100" x2="120" y2="130" stroke="#000" strokeWidth="2" />
                        <line x1="100" y1="100" x2="80" y2="70" stroke="#000" strokeWidth="2" />
                      </svg>
                    )}
                    {materialData.formula === 'TiO2' && (
                      <svg viewBox="0 0 200 200" className="w-full h-full">
                        <circle cx="100" cy="100" r="25" fill="#919191" />
                        <circle cx="130" cy="70" r="15" fill="#E83A3A" />
                        <circle cx="70" cy="70" r="15" fill="#E83A3A" />
                        <circle cx="130" cy="130" r="15" fill="#E83A3A" />
                        <circle cx="70" cy="130" r="15" fill="#E83A3A" />
                        <circle cx="100" cy="60" r="15" fill="#E83A3A" />
                        <circle cx="100" cy="140" r="15" fill="#E83A3A" />
                        <line x1="100" y1="100" x2="130" y2="70" stroke="#000" strokeWidth="2" />
                        <line x1="100" y1="100" x2="70" y2="70" stroke="#000" strokeWidth="2" />
                        <line x1="100" y1="100" x2="130" y2="130" stroke="#000" strokeWidth="2" />
                        <line x1="100" y1="100" x2="70" y2="130" stroke="#000" strokeWidth="2" />
                        <line x1="100" y1="100" x2="100" y2="60" stroke="#000" strokeWidth="2" />
                        <line x1="100" y1="100" x2="100" y2="140" stroke="#000" strokeWidth="2" />
                      </svg>
                    )}
                  </div>
                  <div className="absolute bottom-4 left-4 bg-purple-100 rounded-full px-3 py-1 text-sm flex items-center">
                    <span className="w-4 h-4 rounded-full mr-1 inline-block" style={{ 
                      backgroundColor: materialData.formula.includes('Mn') ? '#8C2D8F' : 
                                      materialData.formula.includes('Fe') ? '#B25D00' : 
                                      materialData.formula.includes('Ti') ? '#919191' : '#666'
                    }}></span>
                    {materialData.formula.split(/(\d+)/).map((part, i) => 
                      isNaN(part) ? part : <sub key={i}>{part}</sub>
                    )}
                  </div>
                  {materialData.formula.includes('O') && (
                    <div className="absolute bottom-4 right-4 bg-red-100 rounded-full px-3 py-1 text-sm flex items-center">
                      <span className="w-4 h-4 rounded-full mr-1 inline-block" style={{ backgroundColor: '#E83A3A' }}></span>
                      O<sub>2</sub>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Properties */}
              <div className="border rounded-md">
                <table className="w-full text-left">
                  <tbody>
                    <tr className="border-b">
                      <th className="px-4 py-2 bg-gray-50">Energy Above Hull</th>
                      <td className="px-4 py-2 text-blue-600">{materialData.formula === 'MnO2' ? '0.000 eV/atom' : '0.031 eV/atom'}</td>
                    </tr>
                    <tr className="border-b">
                      <th className="px-4 py-2 bg-gray-50">Space Group</th>
                      <td className="px-4 py-2 text-blue-600">{materialData.spaceGroup}</td>
                    </tr>
                    <tr className="border-b">
                      <th className="px-4 py-2 bg-gray-50">Band Gap</th>
                      <td className="px-4 py-2">{materialData.bandGap}</td>
                    </tr>
                    <tr className="border-b">
                      <th className="px-4 py-2 bg-gray-50">Predicted Formation Energy</th>
                      <td className="px-4 py-2">{materialData.formationEnergy}</td>
                    </tr>
                    <tr className="border-b">
                      <th className="px-4 py-2 bg-gray-50">Magnetic Ordering</th>
                      <td className="px-4 py-2 text-blue-600">{materialData.magneticOrdering}</td>
                    </tr>
                    <tr className="border-b">
                      <th className="px-4 py-2 bg-gray-50">Total Magnetization</th>
                      <td className="px-4 py-2">{materialData.totalMagnetization}</td>
                    </tr>
                    <tr>
                      <th className="px-4 py-2 bg-gray-50">Experimentally Observed</th>
                      <td className="px-4 py-2">{materialData.experimentallyObserved}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="border rounded-md p-8 text-center text-gray-500 bg-gray-50">
              {loading ? (
                <p>Loading data...</p>
              ) : (
                <p>Enter an ICSD ID to view crystal structure and properties</p>
              )}
            </div>
          )}
          
          {/* Description */}
          {materialData && (
            <div className="mt-6 border rounded-md p-4">
              <h3 className="font-medium text-gray-700 mb-2">Description (Auto-generated)</h3>
              <p className="text-sm text-gray-600">{materialData.description}</p>
            </div>
          )}
        </div>
        
        {/* How to cite */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4"> Sample Table </h2>
          <p className="mb-2"> we will be adding a table for all of sample related experimental properties:</p>
          <div className="pl-4 border-l-4 border-gray-200 py-1">
            <p>Experimental.Catalysis-Hub.org, Cathub Experimental: A Roadmap for Experimental Electrocatalysis Data Sharing, in preparation (2024),</p>
            <p>Kirsten T. Winther, authors ...,</p>
            <p className="italic">other features</p>
            <a href="#" className="text-blue-600 hover:underline">Link to the strcuture</a>
          </div>
        </div>
        
        {/* License */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold mb-4">License</h2>
          <p className="mb-2">Except where otherwise noted, content on Experimental-Catalysis-Hub is licensed under a <a href="#" className="text-blue-600 hover:underline">Creative Commons Attribution 4.0 International License</a>.</p>
          <img src="/api/placeholder/200/50" alt="Creative Commons License" className="mt-2" />
        </div>
      </main>
    </div>
  );
};

export default CrystalStructureViewer;